import { FieldErrors, UseFormRegister } from "react-hook-form";
import get from "lodash.get";

import {
  Field,
  FieldDescription,
  FieldLabel,
} from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";

interface SelectOption {
  value: string;
  label: string;
}

interface FormSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  variant?: "flat" | "bordered" | "faded" | "underlined";
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  size?: "sm" | "md" | "lg";
  radius?: "none" | "sm" | "md" | "lg" | "full";
  isRequired?: boolean;
  disabled?: boolean;
  className?: string;
  description?: string;
  formFieldParams?: {
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    loading: boolean;
    watch: (field: string) => any;
    setValue: (field: string, value: any) => void;
  };
  form?: any;
}

export const FormSelect = ({
  name,
  className,
  label,
  placeholder = "",
  options,
  description = "",
  isRequired = false,
  disabled = false,
  form = null,
  formFieldParams,
  ...rest
}: FormSelectProps) => {
  const errors = [];
  const shortName = name?.split(".").pop();

  let value = "";

  if (formFieldParams) {
    value = formFieldParams.watch(name);
  }

  if (
    shortName &&
    formFieldParams &&
    typeof formFieldParams?.errors[shortName]?.message !== "undefined"
  ) {
    errors.push(formFieldParams?.errors[shortName]?.message);
  }
  if (formFieldParams) {
    const subError = get(formFieldParams?.errors, name);

    if (subError) {
      errors.push(subError.message);
    }
  }

  // If using react-hook-form with form prop (shadcn pattern)
  if (form !== null) {
    return (
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => (
          <FormItem className={className}>
            {label && <FormLabel className="flex gap-1">{label} {isRequired && <span className="text-red-500">*</span>}</FormLabel>}
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={disabled}
            >
              <FormControl className="w-full">
                <SelectTrigger>
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // If using custom formFieldParams pattern
  return (
    <Field className={className}>
      {label && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
      <Select
        value={value ?? ""}
        onValueChange={(newValue) => {
          if (formFieldParams?.setValue) {
            formFieldParams.setValue(name, newValue);
          }
        }}
        disabled={disabled}
        required={!!isRequired}
        {...rest}
      >
        <SelectTrigger id={name}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {description && <FieldDescription>{description}</FieldDescription>}
      {errors.length > 0 && (
        <p className="text-sm font-medium text-destructive mt-2">
          {errors[0]?.toString().replace(/<[^>]*>/g, "")}
        </p>
      )}
    </Field>
  );
};